import { Loader2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useDeleteAccount } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirm-and-erase for the signed-in user's own account. Requires the current
 * password: only email sign-in is enabled in production, so the field is always
 * required here even though OAuth-only accounts wouldn't need one. On success
 * the hook has already cleared the cache and the backend the session cookie, so
 * we just send the (now signed-out) user to the landing page.
 */
export function DeleteAccountDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const deleteMutation = useDeleteAccount();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // The dialog stays mounted in ProfilePage, so a close must drop the typed
  // password (a credential) and any stale error; otherwise they're both
  // pre-populated on reopen. A close request mid-request can't abort it, so
  // ignore it rather than leave the form in an inconsistent state.
  const handleOpenChange = (next: boolean) => {
    if (deleteMutation.isPending) {
      return;
    }
    if (!next) {
      setPassword("");
      setError("");
    }
    onOpenChange(next);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await deleteMutation.mutateAsync({ password });
      navigate("/");
    } catch (err) {
      // ApiError carries the backend message in `body.detail`, e.g.
      // "Current password is incorrect" or "Password confirmation required".
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      setError(detail ?? "Couldn’t delete your account. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete your account</DialogTitle>
          <DialogDescription>
            This permanently erases your account, its credentials, and every assessment you own
            (each one’s report, expert calibration, share links, and invites). Nothing identifying
            survives, and this cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="delete-account-password"
              className="text-foreground text-sm font-medium"
            >
              Confirm your password
            </label>
            <Input
              id="delete-account-password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your current password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              disabled={deleteMutation.isPending}
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete my account"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
